import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import React from 'react';
import type {BoxSelectionProps, DropdownOptionProps, DropdownProps} from 'wix-ui-tpa/cssVars';
import {BoxSelection, BoxSelectionBoxSize, Spinner, SpinnerTypes} from 'wix-ui-tpa/cssVars';
import {Experiments, ProductOptionWidthType, TextualOptionType} from '../../../../../constants';
import {useGlobals, withGlobals} from '../../../../../globalPropsContext';
import {IProduct} from '../../../../../types/galleryTypes';
import {LazyDropDown} from '../../../../lazyComponents/lazyWUTComponents';
import {classes as dropdownOptionStyles, style as styleDropdown} from '../DropdownOption.st.css';
import {DataHook, ProductOptionsChangeData} from '../ProductOptions';
import {classes as boxSelectionStyles} from './ProductTextualOption.st.css';
import s from './ProductTextualOptions.scss';

type IProductOption = IProduct['options'][0];

const renderInnerSelection = (
  selection: DropdownOptionProps,
  index: number,
  selectionIds: number[],
  styles: Record<string, string>
) => (
  <div
    data-hook={`box-selection-text-option-${index}`}
    className={classNames(styles.optionItemText, {
      [styles.disabled]: !selection.isSelectable,
      [styles.selected]: selectionIds.includes(parseInt(selection.id, 10)),
    })}>
    {selection.value}
  </div>
);

const getShownSelectionsAndSuffix = ({
  selections,
  availability,
  showButtons,
  productOptionButtonLimit,
  productUrl,
  isLiveSiteMode,
  isPreviewMode,
  styles,
  t,
}: {
  selections: IProductOption['selections'];
  availability: Record<string, {isVisible: boolean; isSelectable: boolean}>;
  showButtons: boolean;
  productOptionButtonLimit: number;
  productUrl: string;
  isLiveSiteMode: boolean;
  isPreviewMode: boolean;
  styles: Record<string, string>;
  t: IProvidedTranslationProps['t'];
}) => {
  const visibleSelections = selections.filter((selection) => availability[selection.id].isVisible);
  const shownSelections = showButtons ? visibleSelections.slice(0, productOptionButtonLimit) : visibleSelections;
  const remainingOptionsCounter = visibleSelections.length - productOptionButtonLimit;
  const productUrlObj = new URL(`${productUrl}#${VIEW_MORE_OPTIONS_URL_FRAGMENT}-1`);
  const suffix =
    remainingOptionsCounter <= 0 ? null : (
      <a
        dir="ltr"
        href={isLiveSiteMode || isPreviewMode ? productUrlObj.href : null}
        data-hook={DataHook.ButtonOptionMore}
        className={styles.remainingOptionsCounter}
        aria-label={t('buttonOptionNumMoreOptionsAriaLabel', {numRemainingChoices: remainingOptionsCounter})}>
        {t('buttonOptionNumChoicesLeftLabel', {numChoiceLeft: remainingOptionsCounter})}
      </a>
    );
  return {
    suffix,
    shownSelections,
  };
};

export interface TextualOptionProps {
  option: IProductOption;
  availability: Record<string, {isVisible: boolean; isSelectable: boolean}>;
  selectionIds: number[];
  readOnly?: boolean;
  isLoading: boolean;
  productUrl: string;
  onSelectionIdsChange: (optionSelections: number[], changeData: ProductOptionsChangeData) => Promise<void>;
}

const TextualOption: React.FC<TextualOptionProps & IProvidedTranslationProps> = ({
  option,
  availability,
  selectionIds,
  readOnly,
  isLoading,
  productUrl,
  onSelectionIdsChange,
  t,
}) => {
  const {useExperiments, shouldShowMobile, styles, stylesParams, isLiveSiteMode, isPreviewMode} = useGlobals();
  const fitToText = styles.get(stylesParams.gallery_buttonOptionWidthType) === ProductOptionWidthType.FIT;
  const shouldUseNativeDropdownOnMobile = useExperiments.enabled(Experiments.GalleryProductOptionMobileNativeDropdown);
  const isButtonOptionsEnabled = useExperiments.enabled(Experiments.GalleryProductOptionsTextualOptionType);
  const textualOptionType = styles.get(stylesParams.gallery_textualOptionType);
  const useDropdownTpaLabel = useExperiments.enabled(Experiments.UseDropdownTpaLabel);
  const showButtons = isButtonOptionsEnabled && textualOptionType === TextualOptionType.BUTTONS;
  const handleOptionChange = (optionsSelectionIds: number[], id: string) => {
    const nextSelectionIds = [
      ...selectionIds.filter((selectionId) => !optionsSelectionIds.includes(selectionId)),
      parseInt(id, 10),
    ];
    void onSelectionIdsChange(nextSelectionIds, {
      optionType: ProductOptionType.DROP_DOWN,
    });
  };

  const sealButtonChangeHandler = (optionsSelectionIds: number[]): BoxSelectionProps['onChange'] => {
    return ({id}) => {
      const parts = id.split('-');
      const selectionId = parts[parts.length - 1];
      handleOptionChange(optionsSelectionIds, selectionId);
    };
  };

  const sealDropdownChangeHandler =
    (optionsSelectionIds: number[]): DropdownProps['onChange'] =>
    ({id}) => {
      handleOptionChange(optionsSelectionIds, id);
    };

  const shouldAllowCustomProductOptionsAndQuantityWidth = useExperiments.enabled(
    Experiments.GalleryProductOptionsAndQuantityWidth
  );
  const shouldSeparateQuantityAndOptionsStyleParams = useExperiments.enabled(
    Experiments.ShouldSeparateQuantityAndOptionsStyleParams
  );

  const renderLoader = () => {
    return (
      <div data-hook={DataHook.ProductOptionLoader}>
        <Spinner type={SpinnerTypes.slim} className={s.loader} />
      </div>
    );
  };

  const {title, selections, id} = option;
  const productOptionButtonLimit = styles.get(stylesParams.gallery_productOptionButtonChoicesLimit);
  const {suffix, shownSelections} = getShownSelectionsAndSuffix({
    selections,
    availability,
    showButtons,
    productOptionButtonLimit,
    productUrl,
    isLiveSiteMode,
    isPreviewMode,
    styles: s,
    t,
  });

  const options: DropdownOptionProps[] = shownSelections.map((selection) => ({
    id: selection.id.toString(),
    isSelectable: availability[selection.id].isSelectable,
    value: selection.description,
  }));

  const selectionIdsFromOption = selections.map((selection) => selection.id);

  const renderButtonOptions = () =>
    options.map((selection, index) => (
      <BoxSelection.Option
        key={selection.id}
        id={`${option.id}-${selection.id}`}
        unstableHasInnerWrapper={false}
        checked={selectionIds.includes(parseInt(selection.id, 10))}
        aria-label={selection.value}
        disabled={!selection.isSelectable}
        data-hook={`box-selection-option-${index}`}
        className={classNames(boxSelectionStyles.buttonOptionItem, {
          [s.flexGrow]: fitToText,
        })}>
        {renderInnerSelection(selection, index, selectionIds, s)}
      </BoxSelection.Option>
    ));

  const renderBoxSelectionOptions = () => (
    <BoxSelection
      name={`button-option-${id}`}
      aria-label={title}
      size={BoxSelectionBoxSize.xSmall}
      onChange={sealButtonChangeHandler(selectionIdsFromOption)}
      data-hook={DataHook.ButtonOption}
      className={classNames(s.buttonOption, boxSelectionStyles.buttonOptionItem, {
        [s.fitToText]: fitToText,
      })}
      suffix={suffix}>
      {isLoading ? renderLoader() : renderButtonOptions()}
    </BoxSelection>
  );

  const renderDropdownOptions = () => (
    <LazyDropDown
      className={
        /* istanbul ignore next */
        classNames(
          styleDropdown(dropdownOptionStyles.dropdownOption, {
            separateStyles: shouldSeparateQuantityAndOptionsStyleParams,
          }),
          {
            [s.dropdownWidth]: shouldAllowCustomProductOptionsAndQuantityWidth,
            [s.isMobile]: shouldAllowCustomProductOptionsAndQuantityWidth && shouldShowMobile,
          }
        )
      }
      data-hook={DataHook.DropdownOption}
      aria-label={useDropdownTpaLabel ? title : ''}
      placeholder={title}
      options={options}
      onChange={sealDropdownChangeHandler(selectionIdsFromOption)}
      isLoading={isLoading}
      readOnly={readOnly}
      mobileNativeSelect={shouldUseNativeDropdownOnMobile && shouldShowMobile}
    />
  );

  return (
    <div
      data-hook={DataHook.ProductOption}
      className={classNames({[s.dropdownAlignment]: shouldAllowCustomProductOptionsAndQuantityWidth})}>
      {showButtons ? renderBoxSelectionOptions() : renderDropdownOptions()}
    </div>
  );
};

export const ProductTextualOption = withGlobals(withTranslations()(TextualOption));
